import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { identityProvider } from '../../utils';
import { apolloClient, backEndUri } from '../../config';
import queries from '../../queries';
import '../../style/DeviceRemoteControl.scss';

function wsUrlFromBackend(httpUri) {
  if (httpUri.startsWith('https://')) return `wss://${httpUri.slice(8)}`;
  if (httpUri.startsWith('http://')) return `ws://${httpUri.slice(7)}`;
  return httpUri.replace(/^http/, 'ws');
}

/**
 * Contrôle à distance de la WebView sur l'appareil : aperçu JPEG via WebSocket, clics et texte.
 */
class DeviceRemoteControl extends Component {
  constructor(props) {
    super(props);
    this.state = {
      modalOpen: false,
      active: false,
      frameUrl: null,
      error: null,
      status: '',
      textToSend: '',
      shellCommand: '',
      shellLogs: [],
      fullDeviceMode: false,
      openSettingsConfirmVisible: false,
    };
    this.ws = null;
    this._escHandler = this.handleEscape.bind(this);
    this.shellLogRef = React.createRef();
  }

  componentWillUnmount() {
    this.stopSession();
    if (this.state.modalOpen) {
      this.detachModalEffects();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.shellLogs.length !== prevState.shellLogs.length && this.shellLogRef.current) {
      const el = this.shellLogRef.current;
      el.scrollTop = el.scrollHeight;
    }
  }

  handleEscape(e) {
    if (e.key === 'Escape' && this.state.modalOpen) {
      if (this.state.openSettingsConfirmVisible) {
        this.setState({ openSettingsConfirmVisible: false });
        return;
      }
      this.closeModal();
    }
  }

  attachModalEffects() {
    document.addEventListener('keydown', this._escHandler);
    if (document.body.dataset.tskRemotePrevOverflow === undefined) {
      document.body.dataset.tskRemotePrevOverflow = document.body.style.overflow || '';
      document.body.style.overflow = 'hidden';
    }
  }

  detachModalEffects() {
    document.removeEventListener('keydown', this._escHandler);
    if (document.body.dataset.tskRemotePrevOverflow !== undefined) {
      document.body.style.overflow = document.body.dataset.tskRemotePrevOverflow;
      delete document.body.dataset.tskRemotePrevOverflow;
    }
  }

  openModal = () => {
    this.setState({ modalOpen: true, error: null }, () => {
      this.attachModalEffects();
    });
  };

  closeModal = () => {
    this.stopSession();
    this.setState({
      modalOpen: false,
      error: null,
      status: '',
      frameUrl: null,
      textToSend: '',
      shellCommand: '',
      shellLogs: [],
      fullDeviceMode: false,
      openSettingsConfirmVisible: false,
    });
    this.detachModalEffects();
  };

  stopSession = () => {
    const { deviceId } = this.props;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.sendToServer({ command: 'stopRemoteView', deviceId });
      this.ws.close();
    }
    this.ws = null;
    this.setState({
      active: false,
      frameUrl: null,
      status: '',
    });
  };

  sendToServer = (obj) => {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(obj));
    }
  };

  startSession = async () => {
    const { deviceId, connected } = this.props;
    if (!connected) {
      this.setState({ error: 'Appareil non connecté au serveur (aucune session WebSocket).' });
      return;
    }
    const user = identityProvider.currentUser();
    const token = user && (await user.jwt());
    if (!token) {
      this.setState({ error: 'Session expirée ou non authentifié.' });
      return;
    }
    this.setState({ error: null, status: 'Connexion…' });
    const url = `${wsUrlFromBackend(backEndUri)}/ws`;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({
        type: 'viewerSubscribe',
        deviceId,
        authToken: token,
      }));
    };

    ws.onmessage = (ev) => {
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch (e) {
        return;
      }
      if (msg.type === 'viewerSubscribed') {
        this.setState({ active: true, status: 'Démarrage du flux…' }, () => {
          const { fullDeviceMode: fd } = this.state;
          this.sendToServer({
            command: fd ? 'startFullDeviceRemote' : 'startRemoteView',
            deviceId,
          });
        });
      }
      if (msg.type === 'remoteFrame' && msg.imageBase64) {
        this.setState({
          frameUrl: `data:image/jpeg;base64,${msg.imageBase64}`,
          status: 'En direct',
        });
      }
      if (msg.type === 'remoteShellResult') {
        this.setState((prev) => ({
          shellLogs: [
            ...prev.shellLogs,
            {
              id: msg.requestId || `${Date.now()}-${prev.shellLogs.length}`,
              requestId: msg.requestId,
              shell: msg.shell || '',
              exitCode: typeof msg.exitCode === 'number' ? msg.exitCode : -1,
              stdout: msg.stdout || '',
              stderr: msg.stderr || '',
              error: msg.error || '',
            },
          ],
        }));
      }
      if (msg.type === 'viewerError') {
        this.setState({ error: msg.error || 'accès refusé', status: '' });
        ws.close();
      }
    };

    ws.onerror = () => {
      this.setState({ error: 'Erreur WebSocket (réseau ou serveur).', status: '' });
    };

    ws.onclose = () => {
      this.ws = null;
      this.setState((s) => (s.active ? { active: false, frameUrl: null, status: '' } : null));
    };
  };

  normCoords = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = (e.clientX !== undefined ? e.clientX : (e.touches && e.touches[0] ? e.touches[0].clientX : 0));
    const cy = (e.clientY !== undefined ? e.clientY : (e.touches && e.touches[0] ? e.touches[0].clientY : 0));
    const x = (cx - rect.left) / rect.width;
    const y = (cy - rect.top) / rect.height;
    return {
      x: Math.max(0, Math.min(1, x)),
      y: Math.max(0, Math.min(1, y)),
    };
  };

  sendTap = (e) => {
    const { deviceId } = this.props;
    if (!this.state.active) return;
    e.preventDefault();
    e.stopPropagation();
    const { x, y } = this.normCoords(e);
    const { fullDeviceMode } = this.state;
    this.sendToServer({
      command: 'remoteTouch',
      deviceId,
      action: 'tap',
      x,
      y,
      fullScreen: fullDeviceMode,
    });
  };

  sendText = () => {
    const { deviceId } = this.props;
    const { textToSend, active } = this.state;
    if (!active || !textToSend) return;
    this.sendToServer({
      command: 'remoteText',
      deviceId,
      text: textToSend,
    });
  };

  sendShell = () => {
    const { deviceId } = this.props;
    const { shellCommand, active } = this.state;
    if (!active || !shellCommand.trim()) return;
    const requestId = `sh-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    this.sendToServer({
      command: 'remoteShell',
      deviceId,
      shell: shellCommand.trim(),
      requestId,
    });
  };

  clearShellLogs = () => {
    this.setState({ shellLogs: [] });
  };

  requestOpenSystemSettings = () => {
    const { connected } = this.props;
    if (!connected) return;
    this.setState({ openSettingsConfirmVisible: true });
  };

  cancelOpenSettingsConfirm = () => {
    this.setState({ openSettingsConfirmVisible: false });
  };

  confirmOpenSystemSettings = () => {
    const { deviceId } = this.props;
    this.setState({ openSettingsConfirmVisible: false });
    apolloClient
      .mutate({
        mutation: queries.displays.openSystemSettings,
        variables: { id: deviceId },
      })
      .catch(() => {});
  };

  returnToApp = () => {
    const { deviceId, connected } = this.props;
    if (!connected) return;
    apolloClient
      .mutate({
        mutation: queries.displays.returnToApp,
        variables: { id: deviceId },
      })
      .catch(() => {});
  };

  render() {
    const { deviceId, connected } = this.props;
    const {
      modalOpen,
      active,
      frameUrl,
      error,
      status,
      textToSend,
      shellCommand,
      shellLogs,
      fullDeviceMode,
      openSettingsConfirmVisible,
    } = this.state;

    return (
      <div className="tsk-remote-control">
        <button
          type="button"
          className="tsk-remote-trigger"
          onClick={this.openModal}
        >
          <span className="tsk-remote-trigger-icon" aria-hidden>◉</span>
          Contrôle à distance
        </button>

        {modalOpen && (
          <div
            className="tsk-remote-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tsk-remote-modal-heading"
            onClick={this.closeModal}
          >
            <div
              className="tsk-remote-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {openSettingsConfirmVisible && (
                <div
                  className="tsk-remote-inline-confirm"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="tsk-remote-open-settings-confirm-title"
                >
                  <div
                    className="tsk-remote-inline-confirm__backdrop"
                    role="presentation"
                    onClick={this.cancelOpenSettingsConfirm}
                  />
                  <div className="tsk-remote-inline-confirm__panel">
                    <h3
                      id="tsk-remote-open-settings-confirm-title"
                      className="tsk-remote-inline-confirm__title"
                    >
                      <FormattedMessage id="display.mananagement.open_settings" />
                    </h3>
                    <p className="tsk-remote-inline-confirm__text">
                      <FormattedMessage id="display.mananagement.open_settings.warning" />
                    </p>
                    <div className="tsk-remote-inline-confirm__actions">
                      <button
                        type="button"
                        className="tsk-remote-btn-ghost"
                        onClick={this.cancelOpenSettingsConfirm}
                      >
                        <FormattedMessage id="dialog.cancel.button" />
                      </button>
                      <button
                        type="button"
                        className="tsk-remote-btn-primary"
                        onClick={this.confirmOpenSystemSettings}
                      >
                        <FormattedMessage id="display.mananagement.open_settings.confirm" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <header className="tsk-remote-modal-header">
                <div className="tsk-remote-modal-title-block">
                  <h2 id="tsk-remote-modal-heading" className="tsk-remote-modal-title">
                    Contrôle à distance
                  </h2>
                  <p className="tsk-remote-modal-subtitle">
                    WebView ou tout l’écran (root + screencap). Clic sur l’image pour un tap ;
                    texte et shell comme ci-dessous. Mode plein écran : service actif en arrière-plan.
                  </p>
                </div>
                <button
                  type="button"
                  className="tsk-remote-modal-close"
                  onClick={this.closeModal}
                  aria-label="Fermer"
                >
                  ×
                </button>
              </header>

              <div className="tsk-remote-modal-body">
                {!connected && (
                  <span className="tsk-remote-badge tsk-remote-badge--warn">
                    Appareil hors ligne
                  </span>
                )}

                <div className="tsk-remote-status-row">
                  {active && status === 'En direct' && (
                    <span className="tsk-remote-badge tsk-remote-badge--live">
                      <span className="tsk-remote-badge-dot" />
                      {fullDeviceMode ? 'Plein écran' : 'WebView'}
                    </span>
                  )}
                  {status && status !== 'En direct' && (
                    <span className="tsk-remote-badge tsk-remote-badge--off">{status}</span>
                  )}
                  {!active && !status && connected && (
                    <span className="tsk-remote-badge tsk-remote-badge--off">Prêt</span>
                  )}
                </div>

                {error && (
                  <p className="tsk-remote-error">{error}</p>
                )}

                <div className="tsk-remote-actions" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}>
                  {!active && (
                    <label className="tsk-remote-fulldevice-label" htmlFor="tsk-remote-fulldevice">
                      <input
                        id="tsk-remote-fulldevice"
                        type="checkbox"
                        checked={fullDeviceMode}
                        onChange={(e) => this.setState({ fullDeviceMode: e.target.checked })}
                        disabled={!connected}
                      />
                      Plein écran appareil (root requis — screencap + input tap)
                    </label>
                  )}
                  {!active ? (
                    <button
                      type="button"
                      className="tsk-remote-btn-primary"
                      onClick={this.startSession}
                      disabled={!connected}
                    >
                      Démarrer la vue
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="tsk-remote-btn-ghost"
                      onClick={this.stopSession}
                    >
                      Interrompre le flux
                    </button>
                  )}
                  <button
                    type="button"
                    className="tsk-remote-btn-ghost"
                    onClick={this.closeModal}
                  >
                    Fermer
                  </button>
                  <button
                    type="button"
                    className="tsk-remote-btn-ghost"
                    onClick={this.requestOpenSystemSettings}
                    disabled={!connected || openSettingsConfirmVisible}
                  >
                    <FormattedMessage id="display.mananagement.open_settings" />
                  </button>
                  <button
                    type="button"
                    className="tsk-remote-btn-ghost"
                    onClick={this.returnToApp}
                    disabled={!connected || openSettingsConfirmVisible}
                  >
                    <FormattedMessage id="display.mananagement.return_to_app" />
                  </button>
                </div>

                <div className="tsk-remote-viewport">
                  {frameUrl ? (
                    <img
                      src={frameUrl}
                      alt={`Aperçu ${deviceId}`}
                      className="tsk-remote-frame"
                      onClick={this.sendTap}
                    />
                  ) : (
                    <div className="tsk-remote-viewport-placeholder">
                      {active
                        ? 'Réception du flux…'
                        : 'Lancez la vue pour afficher l’écran de l’appareil.'}
                    </div>
                  )}
                </div>

                {active && (
                  <div className="tsk-remote-text-row">
                    <input
                      className="tsk-remote-text-input"
                      type="text"
                      placeholder="Texte à envoyer au focus sur l’appareil"
                      value={textToSend}
                      onChange={(e) => this.setState({ textToSend: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') this.sendText();
                      }}
                    />
                    <button
                      type="button"
                      className="tsk-remote-text-send"
                      onClick={this.sendText}
                      disabled={!textToSend}
                    >
                      Envoyer
                    </button>
                  </div>
                )}

                {active && (
                  <div className="tsk-remote-shell-block">
                    <span className="tsk-remote-shell-label">
                      Commande shell (su -c si root disponible, sinon sh -c)
                    </span>
                    <div className="tsk-remote-shell-input-row">
                      <input
                        className="tsk-remote-shell-input"
                        type="text"
                        autoComplete="off"
                        spellCheck="false"
                        placeholder="Shell : getprop, ls… — Bip : treeosk-beep — Notification (à la place de cmd) : treeosk-notify"
                        value={shellCommand}
                        onChange={(e) => this.setState({ shellCommand: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') this.sendShell();
                        }}
                      />
                      <button
                        type="button"
                        className="tsk-remote-shell-run"
                        onClick={this.sendShell}
                        disabled={!shellCommand.trim()}
                      >
                        Exécuter
                      </button>
                    </div>
                    <div className="tsk-remote-shell-log-toolbar">
                      <span className="tsk-remote-shell-label" style={{ marginBottom: 0 }}>Sortie</span>
                      {shellLogs.length > 0 && (
                        <button
                          type="button"
                          className="tsk-remote-shell-clear"
                          onClick={this.clearShellLogs}
                        >
                          Effacer les logs
                        </button>
                      )}
                    </div>
                    <pre
                      ref={this.shellLogRef}
                      className="tsk-remote-shell-log"
                    >
                      {shellLogs.length === 0 ? (
                        <span className="tsk-remote-shell-log-empty">
                          Aucune sortie pour l’instant. Les réponses (stdout / stderr / code) s’affichent ici.
                        </span>
                      ) : (
                        shellLogs.map((entry) => (
                          <div key={entry.id} className="tsk-remote-shell-entry">
                            <div className="tsk-remote-shell-entry-meta">
                              {entry.shell ? (
                                <>
                                  <span className="tsk-remote-shell-cmd">{entry.shell}</span>
                                  {' · '}
                                </>
                              ) : null}
                              {`exit ${entry.exitCode}`}
                              {entry.requestId ? ` · ${entry.requestId}` : ''}
                            </div>
                            {entry.error ? (
                              <div className="tsk-remote-shell-errline">{entry.error}</div>
                            ) : null}
                            {entry.stdout ? (
                              <>
                                <div className="tsk-remote-shell-section-title">stdout</div>
                                <div className="tsk-remote-shell-stdout">{entry.stdout}</div>
                              </>
                            ) : null}
                            {entry.stderr ? (
                              <>
                                <div className="tsk-remote-shell-section-title">stderr</div>
                                <div className="tsk-remote-shell-stderr">{entry.stderr}</div>
                              </>
                            ) : null}
                            {!entry.stdout && !entry.stderr && !entry.error && entry.exitCode === 0 ? (
                              <div className="tsk-remote-shell-stdout">(aucune sortie)</div>
                            ) : null}
                          </div>
                        ))
                      )}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default DeviceRemoteControl;
